import { useCallback } from 'react';

import type { GridApiPro } from '@mui/x-data-grid-pro';
import { gridColumnFieldsSelector } from '@mui/x-data-grid-pro';

import { useLocalStorage } from '@/hooks/useLocalStorage';

/**
 * Generic hook for managing DataGrid column order with localStorage persistence
 *
 * @param storageKey - Unique key for localStorage (e.g., 'portfolio-column-order')
 * @param defaultOrder - Optional default column order (array of field names)
 * @returns Column order state, setter, and change handler
 */
export const useColumnOrder = (
  storageKey: string,
  defaultOrder: string[] = [],
) => {
  const [columnOrder, setColumnOrder] = useLocalStorage<string[]>(
    storageKey,
    defaultOrder,
  );

  /**
   * Handler for column order changes
   * Uses the gridColumnFieldsSelector to get the current column order from the API ref
   */
  const handleColumnOrderChange = useCallback(
    (apiRef: { current: GridApiPro | null }) => {
      const newOrderedFields = gridColumnFieldsSelector(apiRef);
      setColumnOrder(newOrderedFields);
    },
    [setColumnOrder],
  );

  return {
    columnOrder,
    setColumnOrder,
    handleColumnOrderChange,
  };
};
